// @ts-ignore

import { defineMessages, FormattedMessage } from "react-intl";
import { AppPricingData } from "src/contentful/product-catalog";

import Suspense from "@atlaskit/react-ufo/suspense";

import { ContentText } from "@/components/adg4/price-card/src/content-text";
import { LoadingSpinner } from "@/components/global-nav/user-profile-new/src/components/spinner";
import { PriceFinderMessages } from "@/components/wpl/pricing/src/price-finder/constants";
import { PriceCardProductPlan } from "@/components/wpl/pricing/src/price-finder/price-card/card-container.tsx";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import {
    PriceFinderContextProvider,
    PriceFinderCtaUrlSource
} from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { useLocale } from "@/libraries/web-framework/src/localization/use-locale";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { LoomAiTitle } from "./plan-titles.tsx";
import { SIGNUP_URL_QUERY_PREMIUM } from "./urls.ts";

export type LoomPriceFinderProps = {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
    shouldAddDiscount?: boolean;
    ctaUrlSource?: PriceFinderCtaUrlSource;
};

export const LoomPriceFinder = (props: LoomPriceFinderProps) => {
    const content = {
        free: usePricingPlanComparison("Starter"),
        standard: usePricingPlanComparison("Business"),
        premium: usePricingPlanComparison("Business + AI"),
        enterprise: usePricingPlanComparison("Enterprise")
    };

    const freePlanFeatureMessages = createPlanFeatureMessages(content, "free");
    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );
    const enterprisePlanFeatureMessages = createPlanFeatureMessages(
        content,
        "enterprise"
    );

    const loomProductPlans: PriceCardProductPlan[] = [
        {
            planName: "Starter",
            offeringSku: "loom-free-sku",
            recommendationLimit: Infinity,
            signupLimit: 50000,
            planFeatureMessages: freePlanFeatureMessages,
            description: content.free?.planOneLiner ?? "",
            signupURLSuffix: SIGNUP_URL_QUERY_PREMIUM
        },
        {
            planName: "Business",
            offeringSku: "loom-business-sku",
            recommendationLimit: Infinity,
            signupLimit: 50000,
            planFeatureMessages: standardPlanFeatureMessages,
            description: content.standard?.planOneLiner ?? "",
            signupURLSuffix: SIGNUP_URL_QUERY_PREMIUM
        },
        {
            planName: "Business + AI",
            planTitleComponent: LoomAiTitle,
            offeringSku: "loom-business-ai-sku",
            recommendationLimit: 0,
            signupLimit: 50000,
            planFeatureMessages: premiumPlanFeatureMessages,
            description: content.premium?.planOneLiner ?? "",
            signupURLSuffix: SIGNUP_URL_QUERY_PREMIUM
        },
        {
            planName: "Enterprise",
            offeringSku: "loom-enterprise-sku",
            recommendationLimit: Infinity,
            signupLimit: 50000,
            planFeatureMessages: enterprisePlanFeatureMessages,
            description: content.enterprise?.planOneLiner ?? ""
        }
    ];

    let cardEnterpriseBodyOverride = <div />;

    const locale = useLocale();
    if (locale === "en") {
        cardEnterpriseBodyOverride = (
            <ContentText variant="xl">
                <FormattedMessage
                    id="LoomPriceFinderTeam25.cardEnterpriseBodyOverride"
                    defaultMessage="Let's talk"
                />
            </ContentText>
        );
    }

    const messages: PriceFinderMessages = defineMessages({
        unitCountInputTextRedesign: {
            id: "LoomPriceFinderTeam25.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "LoomPriceFinderTeam25.unitCountInputTerm",
            defaultMessage: "users"
        },
        billingCycleText: {
            id: "LoomPriceFinderTeam25.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        cardOverSignupLimit: {
            id: "LoomPriceFinderTeam25.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "LoomPriceFinderTeam25.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "LoomPriceFinderTeam25.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "LoomPricingPage.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        },
        // @ts-ignore
        cardEnterpriseBodyOverride
    });

    const recommendationElement = (
        <FormattedMessage
            id="LoomPriceFinderTeam25.recommendationElement"
            defaultMessage="Most popular"
        />
    );

    return (
        <Suspense
            interactionName={"loom-price-finder"}
            fallback={LoadingSpinner}
        >
            <PriceFinderContextProvider
                dataProductKey="loom"
                productPlans={loomProductPlans}
                defaultUnitCount={100}
                shouldAddDiscount={props.shouldAddDiscount}
                annualUserTierDisplayThreshold={26}
                recommendationElement={recommendationElement}
                ctaUrlSource={props.ctaUrlSource}
            >
                <PriceFinderSection
                    messages={messages}
                    serverSideCCPPricing={props.pricing?.ccpPricingData?.loom}
                    countryCode={props.countryCode}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};

LoomPriceFinder.displayName = "LoomPriceFinder";
